/**
 * @file useShipmentEvents.ts
 * @description Лента событий доставки одной отгрузки (phase19.13 шаг 6).
 *
 * Две ступени загрузки:
 *  - зонд (`probe`, одно событие) — сразу для переданной отгрузки. Он нужен
 *    ДО раскрытия: по последнему событию считается бейдж «зависла», и без
 *    него карточка нарисовала бы пометку по времени создания отгрузки —
 *    то есть соврала бы;
 *  - полная лента (200 событий) — лениво, при раскрытии блока.
 *
 * Перечитывание привязано к общему флагу `busy` вкладки: любое действие над
 * отгрузкой (в первую очередь «Обновить статус») заканчивается — открытая
 * лента подтягивает новые события сама, руками список не правим.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../../lib/api-client";
import {
  listShipmentEvents,
  type ShipmentEventDto,
} from "../../lib/api-delivery-monitoring";

/** Сколько событий тянем при раскрытии: больше 200 сервер не отдаст. */
const PAGE_LIMIT = 200;

export interface ShipmentEventsState {
  items: ShipmentEventDto[];
  total: number;
  loading: boolean;
  error: string | null;
  /** Полная лента уже читалась (успешно или с ошибкой). */
  loaded: boolean;
  /** Зонд отработал — последнему событию можно верить (в т.ч. его отсутствию). */
  probed: boolean;
  /** Самое свежее событие — источник бейджа «зависла» и левой полосы. */
  lastEvent: ShipmentEventDto | null;
  load: () => void;
  reload: () => void;
}

/**
 * @param shipmentId отгрузка
 * @param busy идёт действие над отгрузкой — по завершении перечитываем
 * @param probe тянуть последнее событие сразу (переданная перевозчику отгрузка)
 */
export function useShipmentEvents(
  shipmentId: string,
  busy = false,
  probe = false,
): ShipmentEventsState {
  const [items, setItems] = useState<ShipmentEventDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [probed, setProbed] = useState(false);
  /** Защита от гонки: ответ устаревшего запроса не должен переписать свежий. */
  const requestRef = useRef(0);

  const fetchPage = useCallback(
    async (limit: number, full: boolean): Promise<void> => {
      const request = requestRef.current + 1;
      requestRef.current = request;
      if (full) setLoading(true);
      try {
        const page = await listShipmentEvents(shipmentId, { limit });
        if (requestRef.current !== request) return;
        setItems(page.items);
        setTotal(page.total);
        setError(null);
      } catch (err) {
        if (requestRef.current !== request) return;
        // Ошибку зонда не показываем: блок свёрнут, ругаться не на что.
        // Развернёт менеджер — полная загрузка покажет причину и «Повторить».
        if (full) {
          setError(
            err instanceof ApiError ? err.message : "Не удалось загрузить историю статусов",
          );
        }
      } finally {
        if (requestRef.current === request) {
          if (full) {
            setLoading(false);
            setLoaded(true);
          }
          setProbed(true);
        }
      }
    },
    [shipmentId],
  );

  const load = useCallback((): void => {
    if (loaded || loading) return;
    void fetchPage(PAGE_LIMIT, true);
  }, [fetchPage, loaded, loading]);

  const reload = useCallback((): void => {
    void fetchPage(PAGE_LIMIT, true);
  }, [fetchPage]);

  // Зонд: последнее событие для бейджа и подписи тоггла.
  useEffect(() => {
    if (probe) void fetchPage(1, false);
  }, [probe, fetchPage]);

  // Действие над отгрузкой завершилось — подтягиваем то, что уже показано.
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) {
      if (loaded) void fetchPage(PAGE_LIMIT, true);
      else if (probe) void fetchPage(1, false);
    }
    wasBusy.current = busy;
  }, [busy, loaded, probe, fetchPage]);

  return {
    items,
    total,
    loading,
    error,
    loaded,
    probed,
    lastEvent: items[0] ?? null,
    load,
    reload,
  };
}
